import type { AxiosInstance } from 'axios';
/**
 * Pre-purchase suitability check (GitLab #58).
 *
 * Endpoint: GET /api/v1/contracts/products/{product_id}/suitability-check
 *
 * Always evaluates against the calling client. Staff callers always
 * see ok=true so the simulator/product-detail pages render the same
 * way for staff demos.
 *
 * The CTA on the simulator + product-detail pages calls this on mount
 * (per product) and disables the "Invest in this product" button when
 * ok=false. The same backend gate also runs on POST /contracts/cases
 * so a client who hits the API directly still gets 403'd — this
 * service exists to keep the *UI* honest, not to be the security
 * boundary.
 */
import authService from './auth.service';

export interface SuitabilityCheckResult {
  ok: boolean;
  /** Short codes the UI maps to user-facing copy. Empty when ok=true.
   *  Today's codes: 'min_monthly_income', 'max_product_risk_level',
   *  'min_net_worth', 'min_investment_experience_years'. New codes can
   *  be added by compliance without a frontend release — unknown codes
   *  fall back to a generic "additional documentation needed" message. */
  missing_requirements: string[];
  suggested_alternative_product_id: string | null;
  suggested_alternative_product_name: string | null;
}

class SuitabilityService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async checkProduct(productId: string): Promise<SuitabilityCheckResult> {
    const res = await this.api.get<SuitabilityCheckResult>(
      `/api/v1/contracts/products/${productId}/suitability-check`,
    );
    return res.data;
  }

  /** Map a backend short-code into a one-line client-facing sentence.
   *  Wording lives here (and not on the backend) so copy iterations
   *  ship as plain frontend changes — no server release for a tone
   *  tweak. */
  describeRequirement(code: string): string {
    switch (code) {
      case 'min_monthly_income':
        return 'Confirm your monthly income on your profile';
      case 'max_product_risk_level':
        return 'This series is above your declared risk tolerance';
      case 'min_net_worth':
        return 'Provide net-worth information on your profile';
      case 'min_investment_experience_years':
        return 'Confirm your investment experience on your profile';
      default:
        return 'Additional documentation is required';
    }
  }
}

export default new SuitabilityService();
